import { Injectable } from '@angular/core';
import { GenderOrPrenatal } from '../api';
import { DateService } from './date.service';
import { TranslateService } from '@ngx-translate/core';
import { isNotEmptyOrWhitespace } from '../utils/stringUtils';
import { Store } from '@ngrx/store';
import { Observable, of, withLatestFrom } from 'rxjs';
import { getPersons, getSaleOfferBegin } from '../states/offer/offer.selector';
import { first, map } from 'rxjs/operators';
import { PersonState } from '../states/offer/offer.state';

export type GenderIcon = 'kid' | 'hair-woman' | 'hair-man' | 'embryo';

@Injectable({
  providedIn: 'root',
})
export class PersonService {
  constructor(
    private translateService: TranslateService,
    private store: Store,
  ) {}

  private static getIconForGender(
    genderOrPrenatal: GenderOrPrenatal,
    dateOfBirth: string,
    saleOfferBegin: string,
  ): GenderIcon {
    const isChild =
      genderOrPrenatal !== GenderOrPrenatal.Prenatal && DateService.isChildAtOfferBegin(dateOfBirth, saleOfferBegin);

    if (isChild) {
      return 'kid';
    } else {
      switch (genderOrPrenatal) {
        case 'FEMALE':
          return 'hair-woman';
        case 'MALE':
          return 'hair-man';
        case 'PRENATAL':
          return 'embryo';
      }
    }
  }

  private static isChild(person: PersonState, saleOfferBegin: string): boolean {
    return (
      person.details.genderOrPrenatal !== GenderOrPrenatal.Prenatal &&
      DateService.isChildAtOfferBegin(person.details.dateOfBirth, saleOfferBegin)
    );
  }

  private static isPrenatal(selectedPerson: PersonState): boolean {
    return selectedPerson.details.genderOrPrenatal === GenderOrPrenatal.Prenatal;
  }

  private static isAdult(selectedPerson: PersonState, saleOfferBegin: string): boolean {
    return !DateService.isChildAtOfferBegin(selectedPerson.details.dateOfBirth, saleOfferBegin);
  }

  public getIconForGender$(genderOrPrenatal: GenderOrPrenatal, dateOfBirth: string): Observable<GenderIcon> {
    return this.store.select(getSaleOfferBegin).pipe(
      first(),
      map((saleOfferBegin) => PersonService.getIconForGender(genderOrPrenatal, dateOfBirth, saleOfferBegin)),
    );
  }

  public getFirstNameOrFallback(firstName: string | undefined, index: number): string {
    return isNotEmptyOrWhitespace(firstName) ? firstName : `${this.translateService.instant('COMMON.PERSON')} ${index}`;
  }

  public getFirstNameOrFallback$(firstName: string | undefined, personId: number): Observable<string> {
    return isNotEmptyOrWhitespace(firstName) ? of(firstName) : this.createFirstNameFallback$(personId);
  }

  public createFirstNameFallback$(personId: number): Observable<string> {
    return this.store.select(getPersons).pipe(
      withLatestFrom(this.store.select(getSaleOfferBegin)),
      first(),
      map(([persons, saleOfferBegin]) => {
        const selectedPerson = persons.find((person) => person.personId === personId) as PersonState;

        if (PersonService.isAdult(selectedPerson, saleOfferBegin)) {
          return this.getFirstNameFallback(
            selectedPerson,
            persons,
            (person: PersonState) => PersonService.isAdult(person, saleOfferBegin),
            'COMMON.PERSON',
          );
        }

        if (PersonService.isPrenatal(selectedPerson)) {
          return this.getFirstNameFallback(
            selectedPerson,
            persons,
            (person: PersonState) => PersonService.isPrenatal(person),
            'COMMON.BABY',
          );
        }

        return this.getFirstNameFallback(
          selectedPerson,
          persons,
          (person: PersonState) => PersonService.isChild(person, saleOfferBegin),
          'COMMON.CHILD',
        );
      }),
    );
  }

  private getFirstNameFallback(
    selectedPerson: PersonState,
    persons: PersonState[],
    filter: (person: PersonState) => boolean,
    translationKey: string,
  ): string {
    const allPersonsWithSameFallback = persons.filter(filter);

    return `${this.translateService.instant(translationKey)} ${allPersonsWithSameFallback.indexOf(selectedPerson) + 1}`;
  }
}
